import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ExternalLink, Loader2, AlertTriangle, Copy, MoreHorizontal, Crown } from "lucide-react";
import { TelegramIcon } from "./BrandIcons";
import ReconnectNeededBadge from "./ReconnectNeededBadge";
import { channelHealthKey, useChannelHealth } from "@/hooks/useChannelHealth";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Switch } from "@/components/ui/switch";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

// Telegram connection card.
//
// Two modes (per businesses.telegram_mode):
//   bot                 → owner pastes a @BotFather token; customers DM that bot.
//   business_connection → owner has Telegram Premium; connects personal account
//                         via /start <token> deeplink with the platform bot.
//
// The card always defaults to bot mode and surfaces the Premium upgrade path
// as a secondary option in the manage dialog.

interface TelegramState {
  mode: "bot" | "business_connection";
  connected: boolean;
  bot: {
    username: string | null;
    connected_at: string | null;
  };
  business_connection: {
    owner_username: string | null;
    connection_id: string | null;
    enabled: boolean;
  };
}

const TelegramConnectionCard = ({ className }: { className?: string }) => {
  const { user, businessId } = useAuth();
  const queryClient = useQueryClient();

  const [setupOpen, setSetupOpen] = useState(false);
  const [tokenInput, setTokenInput] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [startingBC, setStartingBC] = useState(false);
  const [bcDeeplink, setBcDeeplink] = useState<string | null>(null);
  const [replacingToken, setReplacingToken] = useState(false);

  const { data: state, isLoading } = useQuery({
    queryKey: ["telegramConnection", user?.id],
    queryFn: async (): Promise<TelegramState | null> => {
      if (!user) return null;
      const { data, error } = await supabase.functions.invoke("telegram-onboard", {
        method: "GET",
      });
      if (error) throw error;
      return (data as TelegramState) || null;
    },
    enabled: !!user,
    staleTime: 15000,
  });

  const isConnected = !!state?.connected;
  const mode = state?.mode ?? "bot";
  const botUsername = state?.bot?.username;
  const ownerUsername = state?.business_connection?.owner_username;

  const customerDeeplink = botUsername ? `https://t.me/${botUsername}` : "";

  // Telegram rejected the stored bot token (e.g. after BotFather /revoke).
  // Premium mode sends through Dibs's own bot, so only bot mode can get here.
  const { authFailed } = useChannelHealth("telegram");
  const needsNewToken = isConnected && mode === "bot" && authFailed;
  // The token form: always to connect, and while connected to swap in a
  // new token without disconnecting first.
  const showTokenForm = !isConnected || needsNewToken || replacingToken;

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["telegramConnection", user?.id] }),
      queryClient.invalidateQueries({ queryKey: channelHealthKey(businessId, "telegram") }),
    ]);

  const handleConnectBot = async () => {
    const token = tokenInput.trim();
    if (!token) {
      toast.error("Paste your bot token from @BotFather.");
      return;
    }
    if (!/^\d+:[A-Za-z0-9_-]{30,}$/.test(token)) {
      toast.error("That doesn't look like a Telegram bot token. It should look like 1234:AAA...");
      return;
    }
    setConnecting(true);
    try {
      const { data, error } = await supabase.functions.invoke("telegram-onboard", {
        body: { action: "connect_bot", bot_token: token },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success(`Connected to @${data?.bot_username || "your bot"}. Customers can now message it.`);
      setTokenInput("");
      setReplacingToken(false);
      await refresh();
    } catch (err: any) {
      console.error("Telegram connect failed:", err);
      toast.error(err?.message || "Couldn't connect your bot.");
    } finally {
      setConnecting(false);
    }
  };

  const handleStartBusinessConnection = async () => {
    setStartingBC(true);
    setBcDeeplink(null);
    try {
      const { data, error } = await supabase.functions.invoke("telegram-onboard", {
        body: { action: "start_business_connection" },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      const link = data?.deeplink as string | undefined;
      if (!link) throw new Error("No deeplink returned.");
      setBcDeeplink(link);
      window.open(link, "_blank", "noopener,noreferrer");
    } catch (err: any) {
      console.error("Start BC failed:", err);
      toast.error(err?.message || "Couldn't start Premium connection.");
    } finally {
      setStartingBC(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      const { data, error } = await supabase.functions.invoke("telegram-onboard", {
        body: { action: "disconnect" },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success("Telegram disconnected.");
      await refresh();
      setDisconnectOpen(false);
    } catch (err: any) {
      console.error("Telegram disconnect failed:", err);
      toast.error(err?.message || "Couldn't disconnect.");
    } finally {
      setDisconnecting(false);
    }
  };

  const copy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied.`);
    } catch {
      toast.error("Couldn't copy. Select and copy manually.");
    }
  };

  return (
    <>
      <div className={`flex items-center justify-between gap-4 p-4 border rounded-xl bg-card shadow-sm ${className ?? ""}`}>
        <div className="flex items-center gap-4 min-w-0">
          <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-[#229ED9]/10 shrink-0">
            <TelegramIcon className="w-6 h-6" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-sm">Telegram</h3>
              {isConnected && !needsNewToken && (
                <Badge
                  variant="default"
                  className="bg-green-600 hover:bg-green-600 text-white border-transparent"
                >
                  Connected
                </Badge>
              )}
              {needsNewToken && <ReconnectNeededBadge />}
              {mode === "business_connection" && isConnected && (
                <Badge variant="secondary" className="gap-1">
                  <Crown className="w-3 h-3" />
                  Premium
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {needsNewToken
                ? "Telegram rejected your bot token, so replies aren't going out. Paste the new token to fix it."
                : isConnected
                ? "Dibs replies to customers who message you on Telegram."
                : "Connect Telegram so Dibs can reply to customers."}
            </p>
          </div>
        </div>
        <div className="shrink-0">
          {isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : (
            <div className="flex items-center gap-3">
              {needsNewToken && (
                <Button size="sm" variant="outline" onClick={() => setSetupOpen(true)}>
                  Update token
                </Button>
              )}
              <Switch
                checked={isConnected}
                disabled={connecting || disconnecting}
                onCheckedChange={(checked) => {
                  if (checked) {
                    setSetupOpen(true);
                  } else {
                    setDisconnectOpen(true);
                  }
                }}
              />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-muted">
                    <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {isConnected && (
                    <>
                      <DropdownMenuLabel className="font-normal text-muted-foreground">
                        {mode === "bot" && botUsername
                          ? `Connected as @${botUsername}`
                          : mode === "business_connection" && ownerUsername
                            ? `Connected as @${ownerUsername}`
                            : "Connected"}
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem onClick={() => setSetupOpen(true)}>
                    {isConnected ? "Manage connection" : "Connect Telegram"}
                  </DropdownMenuItem>
                  {isConnected && (
                    <DropdownMenuItem
                      onClick={() => setDisconnectOpen(true)}
                      className="text-destructive"
                    >
                      Disconnect
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>

      {/* Setup / manage modal */}
      <Dialog
        open={setupOpen}
        onOpenChange={(open) => {
          setSetupOpen(open);
          if (!open) setReplacingToken(false);
        }}
      >
        <DialogContent className="admin-modal-content sm:max-w-xl">
          <DialogHeader className="admin-modal-header">
            <DialogTitle>
              {needsNewToken
                ? "Update your bot token"
                : isConnected ? "Telegram connection" : "Connect Telegram"}
            </DialogTitle>
            <DialogDescription>
              {needsNewToken
                ? "Replies stopped because Telegram no longer accepts the bot token Dibs has stored."
                : isConnected
                ? "Your Telegram channel is live. Share the customer link below or manage your Premium connection."
                : "Two ways to connect: a dedicated bot (free, recommended) or your personal Telegram (requires Premium)."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            {/* Connected state — share link */}
            {isConnected && mode === "bot" && botUsername && !showTokenForm && (
              <div className="space-y-2">
                <Label>Share this link with your customers</Label>
                <div className="flex gap-2">
                  <Input value={customerDeeplink} readOnly className="font-mono text-xs" />
                  <Button variant="outline" size="icon" onClick={() => copy(customerDeeplink, "Link")}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Anyone who opens this link can start chatting with your AI on Telegram.
                </p>
              </div>
            )}

            {needsNewToken && (
              <Alert className="border-amber-300 bg-amber-50 text-amber-900">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Used /revoke in @BotFather?</AlertTitle>
                <AlertDescription>
                  Paste the new token below. Dibs keeps your conversations and settings.
                </AlertDescription>
              </Alert>
            )}

            {/* Connected bot: swap in a new token without disconnecting */}
            {isConnected && mode === "bot" && !showTokenForm && (
              <Button
                variant="link"
                className="h-auto p-0 text-sm"
                onClick={() => setReplacingToken(true)}
              >
                Replace bot token
              </Button>
            )}

            {/* Bot token flow: connect, or replace the token while connected */}
            {showTokenForm && (
              <>
                {!isConnected && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>How to get your bot token</AlertTitle>
                  <AlertDescription>
                    <ol className="list-decimal pl-5 mt-2 space-y-1 text-sm">
                      <li>
                        Open Telegram and start a chat with{" "}
                        <a
                          href="https://t.me/BotFather"
                          target="_blank"
                          rel="noreferrer"
                          className="underline"
                        >
                          @BotFather
                        </a>
                        .
                      </li>
                      <li>
                        Send <code>/newbot</code>, then follow the prompts to pick a name and a
                        username ending in <code>bot</code> (e.g. <code>your_business_bot</code>).
                      </li>
                      <li>BotFather will reply with an API token. Paste it below.</li>
                    </ol>
                  </AlertDescription>
                </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="telegram-bot-token">Bot API token</Label>
                  <Input
                    id="telegram-bot-token"
                    type="password"
                    placeholder="1234567890:AAH..."
                    value={tokenInput}
                    onChange={(e) => setTokenInput(e.target.value)}
                    autoComplete="off"
                  />
                  <p className="text-xs text-muted-foreground">
                    Stored encrypted. Used to send & receive messages on behalf of your bot.
                  </p>
                </div>
              </>
            )}

            {/* Premium / business_connection upsell (hidden while fixing a token) */}
            {!(isConnected && showTokenForm) && (
            <div className="border rounded-lg p-4 bg-muted/30 space-y-2">
              <div className="flex items-center gap-2">
                <Crown className="w-4 h-4 text-amber-500" />
                <span className="text-sm font-semibold">Use your personal Telegram instead</span>
                <Badge variant="outline" className="text-[10px]">Telegram Premium</Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                If you have Telegram Premium, your AI can reply directly from your personal account —
                customers see messages from you, not from a bot. We&apos;ll open a chat with our
                platform bot to authorize the connection.
              </p>
              {bcDeeplink && (
                <div className="text-xs">
                  <span className="text-muted-foreground">If the popup was blocked, </span>
                  <a href={bcDeeplink} target="_blank" rel="noreferrer" className="underline">
                    open the authorization link manually
                  </a>
                  .
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleStartBusinessConnection}
                disabled={startingBC}
              >
                {startingBC && <Loader2 className="h-3 w-3 animate-spin mr-2" />}
                Connect personal Telegram
              </Button>
            </div>
            )}

            {/* Connected info */}
            {isConnected && state?.bot?.connected_at && (
              <div className="text-xs text-muted-foreground">
                Connected {new Date(state.bot.connected_at).toLocaleDateString()}.
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              className="standard-action-button admin-modal-secondary-button neutral-action-button"
              onClick={() => setSetupOpen(false)}
            >
              Close
            </Button>
            {showTokenForm && (
              <Button
                size="sm"
                className="standard-action-button"
                onClick={handleConnectBot}
                disabled={connecting || !tokenInput.trim()}
              >
                {connecting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {isConnected ? "Update token" : "Connect bot"}
              </Button>
            )}
            {isConnected && botUsername && (
              <a
                href={`https://t.me/${botUsername}`}
                target="_blank"
                rel="noreferrer"
              >
                <Button
                  variant="outline"
                  size="sm"
                  className="standard-action-button admin-modal-secondary-button neutral-action-button"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open @{botUsername}
                </Button>
              </a>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disconnect confirmation */}
      <AlertDialog open={disconnectOpen} onOpenChange={setDisconnectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Telegram?</AlertDialogTitle>
            <AlertDialogDescription>
              Your AI will stop responding to Telegram messages. Existing conversations stay in your
              inbox. You can reconnect any time — just paste your bot token again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={disconnecting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={disconnecting}
              onClick={(e) => {
                e.preventDefault();
                handleDisconnect();
              }}
            >
              {disconnecting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default TelegramConnectionCard;
